import { Head, Link } from "@inertiajs/react"
import { ArrowLeft, Plus, Pencil } from "lucide-react"

import AppLayout from "@/layouts/app-layout"
import {
  cuppingsPath,
  cuppingPath,
  editCuppingPath,
  newCuppingSamplePath,
} from "@/routes"
import type { BreadcrumbItem } from "@/types"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"

interface Sample {
  id: number
  name: string
  description: string
  origin: string
  land: string
  producer: string
  process: number
  variety: number
}

interface User {
  id: number
  name: string
  email: string
}

interface Tasting {
  id: number
  title: string
  details: string
  created_at: string
  samples: Sample[]
  user?: User
}

interface Props {
  tasting: Tasting
}

export default function ShowTasting({ tasting }: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    {
      title: "Catas",
      href: cuppingsPath(),
    },
    {
      title: tasting.title,
      href: cuppingPath(tasting.id),
    },
  ]

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={tasting.title} />

      <div className="flex h-full flex-1 flex-col gap-4 p-4">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Link href={cuppingsPath()}>
                  <Button variant="ghost" size="icon">
                    <ArrowLeft className="h-4 w-4" />
                  </Button>
                </Link>
                <div>
                  <CardTitle>{tasting.title}</CardTitle>
                  <CardDescription>
                    {tasting.details || "Sin descripción"}
                    {tasting.user && (
                      <span className="mt-1 block text-xs">
                        Creado por: {tasting.user.name}
                      </span>
                    )}
                  </CardDescription>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Link href={`/cuppings/${tasting.id}/extrinsic`}>
                  <Button variant="outline">Info Extrinseca</Button>
                </Link>
                <Link href={editCuppingPath(tasting.id)}>
                  <Button variant="outline">
                    <Pencil className="mr-2 h-4 w-4" />
                    Editar
                  </Button>
                </Link>
              </div>
            </div>
          </CardHeader>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>Muestras</CardTitle>
              <CardDescription>
                {tasting.samples?.length || 0} muestras en esta cata
              </CardDescription>
            </div>
            <Link href={newCuppingSamplePath(tasting.id)}>
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                Nueva Muestra
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {!tasting.samples || tasting.samples.length === 0 ? (
              <div className="text-muted-foreground py-12 text-center">
                No hay muestras registradas. Agrega tu primera muestra.
              </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {tasting.samples.map((sample) => (
                  <Card key={sample.id}>
                    <CardHeader>
                      <CardTitle className="text-lg">{sample.name}</CardTitle>
                      <CardDescription className="line-clamp-2">
                        {sample.description || "Sin descripción"}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <div className="flex flex-wrap gap-2">
                        {sample.origin && (
                          <Badge variant="secondary">{sample.origin}</Badge>
                        )}
                        {sample.land && (
                          <Badge variant="outline">{sample.land}</Badge>
                        )}
                      </div>
                      {sample.producer && (
                        <p className="text-muted-foreground text-sm">
                          Productor: {sample.producer}
                        </p>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  )
}
